import type {
  CreateEventInput,
  CreateEventLocationInput,
  CreatePromoInput,
  DeleteEventLocationOutput,
  Event,
  EventLocation,
  Promo,
  UpdateEventInput,
  UpdateEventLocationInput,
  UpdatePromoInput,
} from '@sunrise-ui/api/events';
import { ExperienceType, TemporalRelation } from '@sunrise-ui/api/events';
import { useCacheAvailability, useSnackbar } from '@sunrise-ui/primitives';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { QueryKeys } from '../constants/QueryKeys';
import {
  eventsApi,
  promosApi,
  useAuthMutation,
  useAuthQuery,
} from '../utils/api';
import { assertParameterIsNotUndefinedOrNull } from '../utils/error';

const { eventsKey, temporalKey, locationKey, byBulkKey } = QueryKeys.events;
const { promosKey, byEventKey } = QueryKeys.promos;

export const useGetEvent = (id?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(id, 'id');
      return eventsApi
        .getEventEventsIdGet({
          id,
        })
        .then((response) => response.data);
    },
    queryKey: [eventsKey, id],
    enabled: Boolean(id) && !isQueryKeyAvailable([eventsKey]),
  });
};

export const useGetEventByCommunityId = (communityId?: string) => {
  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await eventsApi.bulkGetEventsByCommunityIdsEventsCommunitiesBulkPost({
          bulkGetEventsByCommunitiesInput: {
            community_ids: [communityId],
          },
        });
      return response.data[0] ?? null;
    },
    queryKey: [eventsKey, 'byCommunityId', communityId],
    enabled: Boolean(communityId),
    staleTime: 300000,
  });
};

export const useUpdateEventCommunity = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    Event,
    unknown,
    { eventId: string; communityId: string; newCommunityId: string }
  >({
    async mutationFn({ eventId, communityId, newCommunityId }) {
      const response =
        await eventsApi.updateEventCommunityEventsEventIdCommunitiesCommunityIdUpdateCommunityPatch(
          {
            eventId,
            communityId,
            updateEventCommunityInput: { new_community_id: newCommunityId },
          },
        );
      return response.data;
    },
    async onSuccess(data) {
      if (data.id) {
        queryClient.setQueryData([eventsKey, data.id], data);
      }
      await queryClient.invalidateQueries({ queryKey: [eventsKey] });
      showSnackbar({ text: 'Event community updated!', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update event community. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    Event,
    unknown,
    { communityId: string; createEventInput: CreateEventInput }
  >({
    async mutationFn({ communityId, createEventInput }) {
      const response =
        await eventsApi.createEventEventsCommunitiesCommunityIdPost({
          communityId,
          createEventInput,
        });
      return response.data;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [eventsKey] });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to create event. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    Event,
    unknown,
    { eventId: string; communityId: string; updateEventInput: UpdateEventInput }
  >({
    async mutationFn({ eventId, communityId, updateEventInput }) {
      const response =
        await eventsApi.updateEventEventsEventIdCommunitiesCommunityIdPut({
          eventId,
          communityId,
          updateEventInput,
        });
      return response.data;
    },
    async onSuccess(data) {
      if (data.id) {
        queryClient.setQueryData([eventsKey, data.id], data);
      }
      await queryClient.invalidateQueries({ queryKey: [eventsKey] });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update event. Please try again.',
        type: 'error',
      });
    },
  });
};

type useGetTemporalEventsArgs = {
  communityId?: string;
  orgId?: string;
  enabled?: boolean;
  experienceTypes?: ExperienceType[];
} & (
  | {
      temporalRelation: TemporalRelation;
      onlyToday?: never;
    }
  | {
      temporalRelation: typeof TemporalRelation.Future;
      onlyToday?: boolean;
    }
);

export const useGetTemporalEvents = ({
  temporalRelation,
  onlyToday,
  communityId,
  orgId,
  enabled,
  experienceTypes = [
    ExperienceType.Sunrise,
    ExperienceType.PrivateRoom,
    ExperienceType.Topic,
    ExperienceType.InPerson,
    ExperienceType.Custom,
    ExperienceType.NoSession,
    ExperienceType.SingleSession,
    ExperienceType.MultiSession,
  ],
}: useGetTemporalEventsArgs) => {
  const queryClient = useQueryClient();
  let select: ((data: Event[]) => Event[]) | undefined;

  if (onlyToday) {
    const now = DateTime.now();
    select = (data) =>
      data?.filter((event) =>
        DateTime.fromISO(event.start_date!).hasSame(now, 'day'),
      );
  }

  const targetCommunityId = communityId ?? orgId;

  return useQuery({
    queryFn: async () => {
      const data = await eventsApi
        .getEventsTemporalEventsTemporalTemporalRelationGet({
          temporalRelation,
          communityId: targetCommunityId,
          experienceTypes,
        })
        .then((response) => response.data);

      data.forEach((event) => {
        queryClient.setQueryData([eventsKey, event.id], event);
      });

      return data;
    },
    enabled,
    queryKey: targetCommunityId
      ? [
          eventsKey,
          temporalKey,
          temporalRelation,
          targetCommunityId,
          'all_experience_types',
        ]
      : [eventsKey, temporalKey, temporalRelation],
    select,
  });
};

export const useGetAllEvents = (communityId?: string) => {
  return useQuery({
    queryFn: async () => {
      return await eventsApi
        .getEventsTemporalEventsTemporalTemporalRelationGet({
          temporalRelation: TemporalRelation.Future,
          communityId,
        })
        .then((response) => response.data);
    },
    queryKey: [eventsKey, 'all', communityId],
    staleTime: 300000, // 5 minutes
  });
};

type UseGetEventsByCommunityIdsArgs = {
  communityIds?: string[];
  enabled?: boolean;
};

/**
 * Fetches all events for multiple community IDs.
 * Uses the bulk events endpoint to get events from an org and all its children.
 */
export const useGetEventsByCommunityIds = ({
  communityIds,
  enabled = true,
}: UseGetEventsByCommunityIdsArgs) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryFn: async () => {
      if (!communityIds?.length) return [];

      const events = await eventsApi
        .bulkGetEventsByCommunityIdsEventsCommunitiesBulkPost({
          bulkGetEventsByCommunitiesInput: {
            community_ids: communityIds,
          },
        })
        .then((response) => response.data);

      // Cache individual events
      events.forEach((event) => {
        queryClient.setQueryData([eventsKey, event.id], event);
      });

      // Sort events by start date (newest first), events without dates go to end
      return events.sort((a, b) => {
        const dateA = a.start_date ? DateTime.fromISO(a.start_date) : null;
        const dateB = b.start_date ? DateTime.fromISO(b.start_date) : null;

        if (!dateA?.isValid && !dateB?.isValid) return 0;
        if (!dateA?.isValid) return 1;
        if (!dateB?.isValid) return -1;

        return dateB.toMillis() - dateA.toMillis();
      });
    },
    queryKey: [eventsKey, 'byCommunityIds', communityIds],
    enabled: enabled && Boolean(communityIds?.length),
    staleTime: 300000,
  });
};

export const useGetEventLocation = (eventLocationId?: string) =>
  useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventLocationId, 'eventLocationId');
      return await eventsApi
        .getEventLocationEventsLocationsEventLocationIdGet({
          eventLocationId,
        })
        .then((response) => response.data);
    },
    queryKey: [eventsKey, locationKey, eventLocationId],
    enabled: Boolean(eventLocationId),
  });

export const useGetBulkEvents = (eventIds?: string[]) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventIds, 'eventIds');

      const events = await eventsApi
        .bulkGetEventsByIdEventsBulkPost({
          bulkGetEventsInput: { event_ids: eventIds },
        })
        .then((response) => response.data);

      events.forEach((event) => {
        queryClient.setQueryData([eventsKey, event.id], event);
      });

      return events;
    },
    queryKey: [eventsKey, byBulkKey, eventIds],
    enabled: Boolean(eventIds?.length),
  });
};

export const useGetEventLocations = (communityId?: string) => {
  return useQuery({
    queryKey: [eventsKey, locationKey, communityId],
    enabled: Boolean(communityId),
    queryFn: async () => {
      const response =
        await eventsApi.listEventLocationsEventsLocationsCommunitiesCommunityIdGet(
          {
            communityId: communityId as string,
          },
        );
      return response.data;
    },
    staleTime: 300000,
  });
};

export const useCreateEventLocation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    EventLocation,
    unknown,
    { communityId: string; createEventLocationInput: CreateEventLocationInput }
  >({
    async mutationFn({ communityId, createEventLocationInput }) {
      const response =
        await eventsApi.createEventLocationEventsLocationsCommunitiesCommunityIdPost(
          {
            communityId,
            createEventLocationInput,
          },
        );
      return response.data;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: [eventsKey, locationKey],
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to create location. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateEventLocation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    EventLocation,
    unknown,
    {
      eventLocationId: string;
      communityId: string;
      updateEventLocationInput: UpdateEventLocationInput;
    }
  >({
    async mutationFn({
      eventLocationId,
      communityId,
      updateEventLocationInput,
    }) {
      const response =
        await eventsApi.updateEventLocationEventsLocationsEventLocationIdCommunitiesCommunityIdPut(
          {
            eventLocationId,
            communityId,
            updateEventLocationInput,
          },
        );
      return response.data;
    },
    async onSuccess(_, variables) {
      // Invalidate specific location
      await queryClient.invalidateQueries({
        queryKey: [eventsKey, locationKey, variables.eventLocationId],
      });
      // Invalidate locations list for the community
      await queryClient.invalidateQueries({
        queryKey: [eventsKey, locationKey, variables.communityId],
      });
      showSnackbar({
        text: 'Location updated!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update location. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useDeleteEventLocation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    DeleteEventLocationOutput,
    unknown,
    { eventLocationId: string; communityId: string }
  >({
    async mutationFn({ eventLocationId, communityId }) {
      const response =
        await eventsApi.deleteEventLocationEventsLocationsEventLocationIdCommunitiesCommunityIdDelete(
          {
            eventLocationId,
            communityId,
          },
        );
      return response.data;
    },
    async onSuccess(_, { eventLocationId }) {
      await queryClient.invalidateQueries({
        queryKey: [eventsKey, locationKey, eventLocationId],
      });
      showSnackbar({
        text: 'Location deleted successfully.',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to delete location. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useGetPromosByEvent = (params?: {
  eventId?: string;
  communityId?: string;
}) =>
  useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(params?.eventId, 'eventId');
      return promosApi
        .getPromosByEventPromosEventsEventIdCommunitiesCommunityIdGet({
          eventId: params?.eventId as string,
          communityId: params?.communityId as string,
        })
        .then((response) => response.data);
    },
    queryKey: [promosKey, byEventKey, params?.eventId, params?.communityId],
    enabled: Boolean(params?.eventId && params?.communityId),
  });

export const useCreatePromo = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useAuthMutation({
    mutationFn: async ({
      communityId,
      createPromoInput,
    }: {
      communityId: string;
      createPromoInput: CreatePromoInput & { discount_code: string };
    }) => {
      const response =
        await promosApi.createPromoPromosCommunitiesCommunityIdPost({
          communityId,
          createPromoInput,
        });
      return response.data;
    },
    onSuccess: (_, { createPromoInput }) => {
      const { event_id: eventId } = createPromoInput;
      // Invalidate promos query for the event to refresh the table
      void queryClient.invalidateQueries({
        queryKey: [promosKey, byEventKey, eventId],
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to create discount code. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdatePromo = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<
    Promo,
    unknown,
    {
      promoId: string;
      communityId: string;
      updateData: UpdatePromoInput & {
        discount_code?: string;
        max_uses?: number;
      };
    }
  >({
    mutationFn: async ({
      promoId,
      communityId,
      updateData,
    }: {
      promoId: string;
      communityId: string;
      updateData: UpdatePromoInput & {
        discount_code?: string;
        max_uses?: number;
      };
    }) => {
      const response =
        await promosApi.updatePromoPromosPromoIdCommunitiesCommunityIdPut({
          promoId,
          communityId,
          updatePromoInput: updateData,
        });
      return response.data;
    },
    onSuccess: (updatedPromo) => {
      const eventId = (updatedPromo as Promo).event_id;
      // Invalidate promos query for the event to refresh the table
      void queryClient.invalidateQueries({
        queryKey: [promosKey, byEventKey, eventId],
      });
    },
    onError: (error: unknown) => {
      // Show more detailed error message if available
      const errorMessage =
        (
          error as {
            response?: { data?: { detail?: string; message?: string } };
            message?: string;
          }
        ).response?.data?.detail ||
        (
          error as {
            response?: { data?: { detail?: string; message?: string } };
            message?: string;
          }
        ).response?.data?.message ||
        (error as { message?: string }).message ||
        'Failed to update discount code. Please try again.';

      showSnackbar({
        text: errorMessage,
        type: 'error',
      });
    },
  });
};
